import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, ChevronDown, ChevronRight } from 'lucide-react';
import Logo from './Logo';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [services, setServices] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Ryd timeout ved unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 300); // 300ms forsinkelse før lukning
  };

  // Hent ydelser fra CMS til dropdown
  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/services')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setServices(data);
        }
      })
      .catch((err) => console.error('Fejl ved hentning af ydelser til menu:', err));
  }, []);

  // Luk mobilmenuen og dropdowns når ruten ændres
  useEffect(() => {
    setIsOpen(false);
    setIsDropdownOpen(false);
    setIsMobileServicesOpen(false);
  }, [location]);

  // Luk dropdown ved klik udenfor
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { name: 'Forside', path: '/' },
    { name: 'Ydelser', path: '/ydelser', hasDropdown: true },
    { name: 'Galleri', path: '/galleri' },
    { name: 'Om os', path: '/om-os' },
    { name: 'Kontakt', path: '/kontakt' },
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Top Bar med kontaktoplysninger */}
      <div className="hidden sm:block bg-blue-950 text-white text-xs py-2 px-4 sm:px-6 border-b border-blue-900/50 relative z-50">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="flex items-center gap-4">
            <a href="tel:+4540942420" className="flex items-center gap-1.5 hover:text-slate-200 transition-colors text-white">
              <Phone className="h-3.5 w-3.5 text-white" />
              <span>+45 40 94 24 20</span>
            </a>
            <a href="mailto:toabyg1@gmail.com" className="flex items-center gap-1.5 hover:text-slate-200 transition-colors text-white">
              <Mail className="h-3.5 w-3.5 text-white" />
              <span>toabyg1@gmail.com</span>
            </a>
          </div>
          <div className="text-white font-medium">
            Din tømrer- og tagspecialist i Ishøj & omegn
          </div>
        </div>
      </div>

      {/* Hovednavigation */}
      <header
        className={`sticky top-0 transition-all duration-300 ${
          isOpen ? 'z-[100]' : 'z-40'
        } ${
          isScrolled
            ? 'bg-white shadow-md py-3'
            : 'bg-white py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-0">
            {/* Top række: Logo på venstre, på mobil: burger på højre, på tablet: CTA på højre */}
            <div className="flex items-center justify-between w-full lg:w-auto">
              <Link to="/" className="flex items-center">
                <Logo className="h-10 sm:h-12" />
              </Link>

              {/* Tablet CTA (synlig på md til lg) */}
              <div className="hidden md:flex lg:hidden items-center">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center px-5 py-2.5 border border-transparent text-sm font-semibold rounded-none text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-sm hover:shadow uppercase tracking-wider"
                >
                  Få et uforpligtende tilbud
                </Link>
              </div>

              {/* Mobil Menu Knap */}
              <div className="md:hidden">
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="inline-flex items-center justify-center p-2 rounded-none text-slate-500 hover:text-black focus:outline-none transition-colors"
                  aria-expanded={isOpen}
                >
                  <span className="sr-only">Åbn menu</span>
                  <Menu className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Navigation og Desktop CTA */}
            <div className="hidden md:flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8 w-full lg:w-auto">
              {/* Desktop/Tablet Navigation - venstrestillet på tablet, højrestillet på desktop */}
              <nav className="flex flex-wrap items-center gap-x-6 gap-y-2 justify-start lg:justify-end">
                {navLinks.map((link) => {
                  if (link.hasDropdown) {
                    return (
                      <div
                        key={link.name}
                        className="relative"
                        ref={dropdownRef}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                      >
                        <button
                          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                          className={`font-semibold text-sm transition-colors relative py-1 uppercase tracking-wider flex items-center gap-1 cursor-pointer ${
                            isActive(link.path)
                              ? 'text-black'
                              : 'text-slate-700 hover:text-black'
                          }`}
                        >
                          <span>{link.name}</span>
                          <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                          {isActive(link.path) && (
                            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-black" />
                          )}
                        </button>

                        {/* Dropdown Menu */}
                        {isDropdownOpen && (
                          <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-slate-200 shadow-xl z-50 py-2 rounded-none animate-in fade-in slide-in-from-top-1 duration-150">
                            {services.length > 0 ? (
                              services
                                .filter((service) => (service.slug?._ || service.slug) !== 'asbesttag')
                                .map((service) => {
                                  const slug = service.slug?._ || service.slug;
                                  const hasSubmenu = slug === 'tagarbejde';
                                  return (
                                    <div key={service.id} className="relative group/item">
                                      <Link
                                        to={`/ydelser/${slug}`}
                                        className="flex items-center justify-between px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-700 hover:text-black hover:bg-slate-50 transition-colors"
                                      >
                                        <span>{service.title}</span>
                                        {hasSubmenu && (
                                          <ChevronRight className="h-3.5 w-3.5 text-slate-400 group-hover/item:text-black transition-colors" />
                                        )}
                                      </Link>
                                      {hasSubmenu && (
                                        <div className="absolute left-full top-0 -mt-2 w-56 bg-white border border-slate-200 shadow-xl py-2 rounded-none hidden group-hover/item:block animate-in fade-in slide-in-from-left-1 duration-150">
                                          <Link
                                            to="/ydelser/asbesttag"
                                            className="block px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-700 hover:text-black hover:bg-slate-50 transition-colors"
                                          >
                                            Asbesttag
                                          </Link>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })
                            ) : (
                              <div className="px-4 py-2 text-xs text-slate-400">Henter ydelser...</div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <Link
                      key={link.name}
                      to={link.path}
                      className={`font-semibold text-sm transition-colors relative py-1 uppercase tracking-wider ${
                        isActive(link.path)
                          ? 'text-black'
                          : 'text-slate-700 hover:text-black'
                      }`}
                    >
                      {link.name}
                      {isActive(link.path) && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-black" />
                      )}
                    </Link>
                  );
                })}
              </nav>

              {/* Desktop CTA (kun synlig på lg og op) */}
              <div className="hidden lg:block">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center px-5 py-2.5 border border-transparent text-sm font-semibold rounded-none text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-sm hover:shadow uppercase tracking-wider"
                >
                  Få et uforpligtende tilbud
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Mobil Navigation Sidebar Drawer */}
        {isOpen && (
          <div className="fixed inset-0 z-[100] md:hidden" role="dialog" aria-modal="true">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
              onClick={() => setIsOpen(false)}
            />

            {/* Sidebar Panel */}
            <div className="fixed inset-y-0 right-0 w-full max-w-xs bg-white shadow-2xl flex flex-col z-50 animate-in slide-in-from-right duration-300">
              {/* Sidebar Header */}
              <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-white">
                <Link to="/" onClick={() => setIsOpen(false)} className="flex items-center">
                  <Logo className="h-9" />
                </Link>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 rounded-none text-slate-500 hover:text-black transition-colors focus:outline-none"
                >
                  <span className="sr-only">Luk menu</span>
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Sidebar Content (Scrollable) */}
              <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
                <nav className="space-y-1">
                  {navLinks.map((link) => {
                    if (link.hasDropdown) {
                      return (
                        <div key={link.name} className="space-y-1">
                          <button
                            onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                            className={`w-full flex items-center justify-between px-3 py-3 rounded-none text-base font-bold transition-colors uppercase tracking-wider text-left ${
                              isActive(link.path)
                                ? 'text-black border-l-2 border-black pl-2.5'
                                : 'text-slate-800 hover:bg-slate-50'
                            }`}
                          >
                            <span>{link.name}</span>
                            <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${isMobileServicesOpen ? 'rotate-180' : ''}`} />
                          </button>
                          
                          {/* Undermenu for ydelser på mobil - kun synlig når isMobileServicesOpen er true */}
                          {isMobileServicesOpen && (
                            <div className="pl-4 border-l border-slate-200 space-y-1 py-1 animate-in fade-in slide-in-from-top-1 duration-150">
                              {services
                                .filter((service) => (service.slug?._ || service.slug) !== 'asbesttag')
                                .map((service) => {
                                  const slug = service.slug?._ || service.slug;
                                  const isTagarbejde = slug === 'tagarbejde';
                                  return (
                                    <div key={service.id} className="space-y-1">
                                      <Link
                                        to={`/ydelser/${slug}`}
                                        onClick={() => setIsOpen(false)}
                                        className="block px-3 py-2 text-xs font-semibold text-slate-600 hover:text-black uppercase tracking-wider transition-colors"
                                      >
                                        {service.title}
                                      </Link>
                                      {isTagarbejde && (
                                        <div className="pl-4 border-l border-slate-100">
                                          <Link
                                            to="/ydelser/asbesttag"
                                            onClick={() => setIsOpen(false)}
                                            className="block px-3 py-1.5 text-[11px] font-semibold text-slate-500 hover:text-black uppercase tracking-wider transition-colors"
                                          >
                                            — Asbesttag
                                          </Link>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                            </div>
                          )}
                        </div>
                      );
                    }

                    return (
                      <Link
                        key={link.name}
                        to={link.path}
                        onClick={() => setIsOpen(false)}
                        className={`block px-3 py-3 rounded-none text-base font-bold transition-colors uppercase tracking-wider ${
                          isActive(link.path)
                            ? 'bg-slate-100 text-black border-l-2 border-black pl-2.5'
                            : 'text-slate-800 hover:bg-slate-50 hover:text-black'
                        }`}
                      >
                        {link.name}
                      </Link>
                    );
                  })}
                </nav>
              </div>

              {/* Sidebar Footer */}
              <div className="p-6 border-t border-slate-100 bg-slate-50 space-y-4">
                <div className="space-y-2.5">
                  <a href="tel:+4540942420" className="flex items-center gap-2.5 text-sm font-semibold text-slate-700 hover:text-black transition-colors">
                    <Phone className="h-4 w-4 text-slate-500" />
                    <span>+45 40 94 24 20</span>
                  </a>
                  <a href="mailto:toabyg1@gmail.com" className="flex items-center gap-2.5 text-sm font-semibold text-slate-700 hover:text-black transition-colors">
                    <Mail className="h-4 w-4 text-slate-500" />
                    <span>toabyg1@gmail.com</span>
                  </a>
                </div>
                <Link
                  to="/kontakt"
                  onClick={() => setIsOpen(false)}
                  className="block w-full text-center px-5 py-3 border border-transparent text-sm font-semibold rounded-none text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow uppercase tracking-wider"
                >
                  Få et uforpligtende tilbud
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
